/**
 * Free-element body drags: translate on the plan, move in 3D from the gadget,
 * and rotate about the center from the rotation handle. Side resizes are
 * perpendicular drags (perpendicularDrag).
 */

import {
  clamp,
  FloorPlanRoom,
  geometryBounds,
  movesInHeight,
  Point,
  roomCeilingHeight,
  setFreeElementBase,
  setFreeElementFrame,
  setFreeElementRotation,
} from '@kaiizen/kai-floor-plan';
import type { EditorMutableState, Gesture } from '../editorState';
import { freeElementSpan, snapBase, snapEdges, snapFrame } from '../elementSnapping';
import { select } from '../selection';
import { snapReach } from '../snapReach';
import { beginDraft } from './draft';

type MoveGesture = Extract<Gesture, { kind: 'moveFree' }>;
type RotateGesture = Extract<Gesture, { kind: 'rotateFree' }>;

/** Translate a free element by its body. */
export function beginFreeMove(state: EditorMutableState, index: number, model: Point): void {
  select(state, { kind: 'inner', index });
  const element = state.shape.freeElements?.[index];
  if (!element) return;
  beginDraft(state);
  state.drag = { kind: 'moveFree', index, grabX: model.x - element.x, grabY: model.y - element.y };
}

/**
 * A free element placed at (x, y): clamped to the room, and, unrotated with
 * snapping on, snapped onto the edges it would bump into.
 */
function placeFreeElement(state: EditorMutableState, base: FloorPlanRoom, index: number, x: number, y: number, reach: number): FloorPlanRoom {
  const element = base.freeElements?.[index];
  if (!element) return base;
  const bounds = geometryBounds(base);
  x = clamp(x, bounds.minX, bounds.maxX - element.width);
  y = clamp(y, bounds.minY, bounds.maxY - element.length);
  if (state.snapping && !element.rotation) {
    const snapped = snapFrame({ x, y, width: element.width, length: element.length }, snapEdges(base, index), reach);
    x = clamp(snapped.x, bounds.minX, bounds.maxX - element.width);
    y = clamp(snapped.y, bounds.minY, bounds.maxY - element.length);
  }
  return setFreeElementFrame(base, index, { x, y, width: element.width, length: element.length });
}

/** Advance a body move: the element follows the pointer by where it was grabbed. */
export function moveFreeElement(state: EditorMutableState, gesture: MoveGesture, model: Point): void {
  state.draftShape = placeFreeElement(
    state,
    state.shape,
    gesture.index,
    model.x - gesture.grabX,
    model.y - gesture.grabY,
    snapReach(state.viewport.scale),
  );
}

/** Start moving a free element from the 3D gadget; `moveFree3D` advances it by how far the drag has come. */
export function beginFreeMove3D(state: EditorMutableState, index: number): void {
  select(state, { kind: 'inner', index });
  const element = state.shape.freeElements?.[index];
  if (!element || state.readOnly) return;
  beginDraft(state);
  state.drag = { kind: 'moveFree3D', index, start: { x: element.x, y: element.y, base: element.base ?? 0 } };
}

/**
 * Advance a gadget drag: the element goes to where it began plus `delta`
 * (plan feet x / y, height z), clamped and snapped like a plan move, and in
 * height kept within the room and snapped to the floor, the ceiling, or an
 * overlapping element's top or bottom. `reach` is the snap reach at the
 * gadget's depth on screen. Flooring and walls stay put in height
 * (`movesInHeight`).
 */
export function moveFree3D(state: EditorMutableState, delta: { x: number; y: number; z: number }, reach: number): void {
  const gesture = state.drag;
  if (!gesture || gesture.kind !== 'moveFree3D' || !state.draftShape) return;
  const base = state.shape;
  const element = base.freeElements?.[gesture.index];
  if (!element) return;
  let shape = placeFreeElement(state, base, gesture.index, gesture.start.x + delta.x, gesture.start.y + delta.y, reach);
  if (movesInHeight(element)) {
    const ceiling = roomCeilingHeight(base);
    const [bottom, top] = freeElementSpan(element, ceiling);
    const height = top - bottom;
    const within = (z: number): number => Math.max(0, ceiling > 0 ? Math.min(z, Math.max(0, ceiling - height)) : z);
    let z = within(gesture.start.base + delta.z);
    if (state.snapping) z = within(snapBase(base, gesture.index, z, height, reach));
    shape = setFreeElementBase(shape, gesture.index, z);
  }
  state.draftShape = shape;
}

/** Rotate a free element about its center via the rotation handle. */
export function beginFreeRotate(state: EditorMutableState, index: number): void {
  select(state, { kind: 'inner', index });
  const element = state.shape.freeElements?.[index];
  if (!element) return;
  beginDraft(state);
  state.drag = { kind: 'rotateFree', index, cx: element.x + element.width / 2, cy: element.y + element.length / 2 };
}

/**
 * Advance a rotation. The handle rides the element's local 0° direction, so
 * the pointer's bearing from the center IS the rotation, snapped to 5° steps.
 */
export function rotateFreeElement(state: EditorMutableState, gesture: RotateGesture, model: Point): void {
  const degrees = (Math.atan2(model.y - gesture.cy, model.x - gesture.cx) * 180) / Math.PI;
  state.draftShape = setFreeElementRotation(state.shape, gesture.index, Math.round(degrees / 5) * 5);
}
